/**
 * Hydrates every `[data-pag-recently-viewed]` instance on the page (home,
 * PDP, cart-drawer-empty, search-empty, 404, empty-collection) with cards
 * for the shopper's recently viewed products.
 *
 * No empty shells: a rail with fewer than its `data-min-products` threshold
 * makes no request and stays hidden, so there is no layout shift and no
 * "0 items" box for a new visitor.
 *
 * Cards come from assets/pag-product-hydrate.js (`fetchProductCards`), the
 * same Section Rendering API trick predictive search's own recently-viewed
 * strip uses — real server-rendered `pag-mini-card` markup, draft/deleted
 * products silently excluded.
 */

import { RecentlyViewed } from '@theme/recently-viewed-products';
import { fetchProductCards, announceHydrated } from '@theme/pag-product-hydrate';

const SELECTOR = '[data-pag-recently-viewed]';

// Tracks sections this module has already hydrated, so the internal
// `shopify:section:load` dispatch below (for pag-rail.js) doesn't loop back
// through the listener at the bottom of this file and re-hydrate itself.
const hydratedSections = new WeakSet();

/** @param {HTMLElement} section */
async function hydrate(section) {
  const track = section.querySelector('[data-rail]');
  if (!track) return;

  const excludeId = section.dataset.excludeId ?? '';
  const minProducts = Number(section.dataset.minProducts) || 1;
  const maxProducts = Number(section.dataset.maxProducts) || 0;

  let ids = RecentlyViewed.getProducts().filter((id) => id !== excludeId);

  if (ids.length < minProducts) {
    section.hidden = true;
    track.replaceChildren();
    return;
  }

  if (maxProducts > 0) ids = ids.slice(0, maxProducts);

  let cards;
  try {
    cards = await fetchProductCards(ids, { section: 'pag-product-hydrate-card' });
  } catch {
    // Network hiccup: leave whatever was already there (or stay hidden) rather
    // than show a broken rail.
    return;
  }

  if (cards.length < minProducts) {
    section.hidden = true;
    track.replaceChildren();
    return;
  }

  track.replaceChildren(...cards);
  announceHydrated(section);
  hydratedSections.add(section);
  // Lets assets/pag-rail.js pick up the freshly inserted [data-rail] track —
  // it listens for this event globally and re-inits scroll-arrow state for
  // whatever section dispatched it.
  section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
  section.hidden = false;
}

/** @param {ParentNode} [root] */
function hydrateAll(root = document) {
  if (root instanceof Element && root.matches(SELECTOR)) hydrate(root);
  root.querySelectorAll?.(SELECTOR).forEach((section) => hydrate(/** @type {HTMLElement} */ (section)));
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-pag-recently-viewed-clear]') : null;
  if (!button) return;
  RecentlyViewed.clearProducts();
});

document.addEventListener(RecentlyViewed.CHANGE_EVENT, () => hydrateAll());

// Horizon dispatches this after morphing in fresh section content (predictive
// search's empty state re-renders this way on every reset) — covers a
// [data-pag-recently-viewed] instance that did not exist when this module
// first ran. The `hydratedSections` check above stops this from looping back
// on the dispatch this module fires for pag-rail.js right after hydrating.
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof Element && target.matches(SELECTOR) && hydratedSections.has(target)) return;
  hydrateAll(target);
});

hydrateAll();
